import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Loader2, Mail, Clock, Globe, X, Plus, CalendarDays, Heart, FlaskConical, CheckCircle, AlertCircle, Wifi, WifiOff, ShieldAlert, Lock, Copy, ExternalLink } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Generate a weekly schedule from legacy booking_hours settings
function generateScheduleFromLegacy(s) {
  const schedule = {};
  for (let d = 0; d < 7; d++) {
    const dayKey = String(d);
    const activeDays = s.booking_days || [0, 1, 2, 3, 4, 5, 6];
    if (!activeDays.includes(d)) {
      schedule[dayKey] = [];
    } else {
      const start = s.booking_hours_start ?? 9;
      const end = s.booking_hours_end ?? 17;
      const dur = s.slot_duration_hours || 1;
      const times = [];
      for (let h = start; h + dur <= end; h += dur) {
        times.push(`${String(Math.floor(h)).padStart(2, "0")}:${String(Math.round((h % 1) * 60)).padStart(2, "0")}`);
      }
      schedule[dayKey] = times;
    }
  }
  return schedule;
}

// Per-day slot editor
function WeeklyScheduleEditor({ schedule, onChange }) {
  const [inputs, setInputs] = useState(() => {
    const init = {};
    for (let d = 0; d < 7; d++) init[String(d)] = "09:00";
    return init;
  });

  const addSlot = (dayKey) => {
    const time = inputs[dayKey];
    if (!time) return;
    const existing = schedule[dayKey] || [];
    if (existing.includes(time)) {
      toast.info(`${time} is already added for this day`);
      return;
    }
    const sorted = [...existing, time].sort();
    onChange({ ...schedule, [dayKey]: sorted });
  };

  const removeSlot = (dayKey, time) => {
    const existing = schedule[dayKey] || [];
    onChange({ ...schedule, [dayKey]: existing.filter((t) => t !== time) });
  };

  const clearDay = (dayKey) => {
    onChange({ ...schedule, [dayKey]: [] });
  };

  return (
    <div className="space-y-3">
      {DAYS.map((dayName, i) => {
        const dayKey = String(i);
        const daySlots = schedule[dayKey] || [];
        const isOpen = daySlots.length > 0;

        return (
          <div
            key={dayKey}
            data-testid={`day-schedule-${dayKey}`}
            className={`rounded-xl border p-4 transition-colors ${isOpen ? "border-green-200 bg-white" : "border-slate-100 bg-slate-50"}`}
          >
            {/* Day header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="font-semibold text-slate-800 w-28">{dayName}</span>
                {isOpen ? (
                  <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                    {daySlots.length} slot{daySlots.length !== 1 ? "s" : ""}
                  </span>
                ) : (
                  <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Closed</span>
                )}
              </div>
              {isOpen && (
                <button
                  type="button"
                  onClick={() => clearDay(dayKey)}
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                  data-testid={`clear-day-${dayKey}`}
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Slot chips */}
            {daySlots.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {daySlots.map((time) => (
                  <span
                    key={time}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 border border-green-200 text-green-800 rounded-full text-sm font-medium"
                    data-testid={`slot-chip-${dayKey}-${time}`}
                  >
                    {time}
                    <button
                      type="button"
                      onClick={() => removeSlot(dayKey, time)}
                      className="text-green-500 hover:text-red-500 transition-colors ml-0.5"
                      data-testid={`remove-slot-${dayKey}-${time}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add slot row */}
            <div className="flex items-center gap-2">
              <input
                type="time"
                value={inputs[dayKey]}
                onChange={(e) => setInputs({ ...inputs, [dayKey]: e.target.value })}
                data-testid={`time-input-${dayKey}`}
                className="h-9 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-800 focus:outline-none focus:border-green-600 focus:bg-white"
              />
              <Button
                type="button"
                size="sm"
                onClick={() => addSlot(dayKey)}
                data-testid={`add-slot-btn-${dayKey}`}
                className="bg-[#15803d] hover:bg-green-800 text-white text-sm rounded-full px-4 gap-1.5"
              >
                <Plus className="w-3.5 h-3.5" /> Add Slot
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Blocked Times Editor for special events
function BlockedTimesEditor({ blockedTimes, onUpdate }) {
  const [newBlock, setNewBlock] = useState({
    date: "",
    start_time: "",
    end_time: "",
    reason: "",
    allDay: true
  });
  const [adding, setAdding] = useState(false);

  const handleAdd = async () => {
    if (!newBlock.date) {
      toast.error("Please select a date");
      return;
    }
    if (!newBlock.allDay && (!newBlock.start_time || !newBlock.end_time)) {
      toast.error("Please specify both start and end times, or select 'All Day'");
      return;
    }
    if (!newBlock.allDay && newBlock.start_time >= newBlock.end_time) {
      toast.error("Start time must be before end time");
      return;
    }

    setAdding(true);
    try {
      const payload = {
        date: newBlock.date,
        start_time: newBlock.allDay ? null : newBlock.start_time,
        end_time: newBlock.allDay ? null : newBlock.end_time,
        reason: newBlock.reason || null
      };
      const res = await api.addBlockedTime(payload);
      onUpdate(res.data.blocked_times);
      setNewBlock({ date: "", start_time: "", end_time: "", reason: "", allDay: true });
      toast.success("Blocked time added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add blocked time");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (block) => {
    try {
      const res = await api.removeBlockedTime({
        date: block.date,
        start_time: block.start_time,
        end_time: block.end_time
      });
      onUpdate(res.data.blocked_times);
      toast.success("Blocked time removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove blocked time");
    }
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + "T00:00:00");
      return d.toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const formatTimeRange = (block) => {
    if (!block.start_time && !block.end_time) return "All Day";
    return `${block.start_time} - ${block.end_time}`;
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="space-y-4">
      {/* Existing blocked times */}
      {blockedTimes && blockedTimes.length > 0 ? (
        <div className="space-y-2">
          {blockedTimes.map((block, idx) => (
            <div
              key={`${block.date}-${block.start_time || 'all'}-${idx}`}
              className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg"
              data-testid={`blocked-time-${block.date}`}
            >
              <div className="flex-1">
                <div className="font-medium text-red-800">
                  {formatDate(block.date)}
                </div>
                <div className="text-sm text-red-600">
                  {formatTimeRange(block)}
                  {block.reason && <span className="ml-2 text-red-500">— {block.reason}</span>}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemove(block)}
                className="text-red-600 hover:text-red-800 hover:bg-red-100"
                data-testid={`remove-blocked-${block.date}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500 italic">No blocked times configured</p>
      )}

      {/* Add new blocked time */}
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
        <h4 className="text-sm font-medium text-slate-700 mb-3">Add New Blocked Time</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-slate-600 text-sm mb-1 block">Date</Label>
            <Input
              type="date"
              min={today}
              value={newBlock.date}
              onChange={(e) => setNewBlock({ ...newBlock, date: e.target.value })}
              className="h-10 bg-white"
              data-testid="blocked-date-input"
            />
          </div>
          <div>
            <Label className="text-slate-600 text-sm mb-1 block">Reason (optional)</Label>
            <Input
              type="text"
              placeholder="e.g., Special Event"
              value={newBlock.reason}
              onChange={(e) => setNewBlock({ ...newBlock, reason: e.target.value })}
              className="h-10 bg-white"
              maxLength={200}
              data-testid="blocked-reason-input"
            />
          </div>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={newBlock.allDay}
              onChange={(e) => setNewBlock({ ...newBlock, allDay: e.target.checked })}
              className="w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
              data-testid="blocked-allday-checkbox"
            />
            <span className="text-sm text-slate-700">Block entire day</span>
          </label>
        </div>

        {!newBlock.allDay && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-600 text-sm mb-1 block">Start Time</Label>
              <Input
                type="time"
                value={newBlock.start_time}
                onChange={(e) => setNewBlock({ ...newBlock, start_time: e.target.value })}
                className="h-10 bg-white"
                data-testid="blocked-start-time"
              />
            </div>
            <div>
              <Label className="text-slate-600 text-sm mb-1 block">End Time</Label>
              <Input
                type="time"
                value={newBlock.end_time}
                onChange={(e) => setNewBlock({ ...newBlock, end_time: e.target.value })}
                className="h-10 bg-white"
                data-testid="blocked-end-time"
              />
            </div>
          </div>
        )}

        <Button
          type="button"
          onClick={handleAdd}
          disabled={adding || !newBlock.date}
          className="mt-4 bg-red-600 hover:bg-red-700 text-white"
          data-testid="add-blocked-time-btn"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          Block Time
        </Button>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const isSuperAdmin = localStorage.getItem("admin_is_super") === "1";
  const [settings, setSettings] = useState(null);
  const [weeklySchedule, setWeeklySchedule] = useState({});
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smtpPassword, setSmtpPassword] = useState("");
  const [availabilityPassword, setAvailabilityPassword] = useState("");
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);

  const testSmtpConnection = async () => {
    setSmtpTesting(true);
    setSmtpTestResult(null);
    try {
      const res = await api.testSmtpConnection();
      setSmtpTestResult(res.data);
      if (res.data.success) {
        toast.success("SMTP connection successful!");
      } else {
        toast.error(res.data.error || "SMTP connection failed");
      }
    } catch (err) {
      setSmtpTestResult({
        success: false,
        error: err.response?.data?.detail || "Failed to test connection",
        steps: []
      });
      toast.error("Failed to test SMTP connection");
    } finally {
      setSmtpTesting(false);
    }
  };

  useEffect(() => {
    api.getSettings()
      .then((r) => {
        const s = r.data;
        setSettings(s);
        // Use weekly_schedule if present, otherwise migrate from legacy settings
        if (s.weekly_schedule && Object.keys(s.weekly_schedule).length > 0) {
          setWeeklySchedule(s.weekly_schedule);
        } else {
          setWeeklySchedule(generateScheduleFromLegacy(s));
        }
        // Load blocked times
        setBlockedTimes(s.blocked_times || []);
      })
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send the fields that can be updated - exclude read-only fields like smtp_password_set
      const payload = {
        booking_hours_start: settings.booking_hours_start,
        booking_hours_end: settings.booking_hours_end,
        booking_days: settings.booking_days,
        slot_duration_hours: settings.slot_duration_hours,
        min_advance_hours: settings.min_advance_hours,
        gap_minutes: settings.gap_minutes,
        smtp_host: settings.smtp_host,
        smtp_port: settings.smtp_port,
        smtp_user: settings.smtp_user,
        smtp_sender_email: settings.smtp_sender_email,
        smtp_sender_name: settings.smtp_sender_name,
        smtp_use_tls: settings.smtp_use_tls,
        timezone: settings.timezone,
        frontend_url: settings.frontend_url,
        weekly_schedule: weeklySchedule,
        embed_config: settings.embed_config,
        email_templates: settings.email_templates,
        sponsor_message_enabled: settings.sponsor_message_enabled,
        sponsor_message: settings.sponsor_message,
        sponsor_url: settings.sponsor_url,
        booking_rate_limit_enabled: settings.booking_rate_limit_enabled,
        maintenance_mode: settings.maintenance_mode,
        availability_enabled: settings.availability_enabled,
      };
      
      // Only include smtp_password if user entered a new one
      if (smtpPassword && smtpPassword.trim()) {
        payload.smtp_password = smtpPassword;
      }
      // Only include availability_password if user entered a new one
      if (availabilityPassword && availabilityPassword.trim()) {
        payload.availability_password = availabilityPassword;
      }
      
      await api.updateSettings(payload);
      toast.success("Settings saved successfully");
      setSmtpPassword("");
      setAvailabilityPassword("");
      // Update the local state to reflect password is now set
      if (smtpPassword && smtpPassword.trim()) {
        setSettings(prev => ({ ...prev, smtp_password_set: true }));
      }
      if (availabilityPassword && availabilityPassword.trim()) {
        setSettings(prev => ({ ...prev, availability_password_set: true }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-8 h-8 animate-spin text-green-700" />
    </div>
  );
  if (!settings) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Settings</h1>
          <p className="text-slate-500 mt-1">Configure per-day time slots, SMTP email, and system preferences</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-settings-btn"
          className="gap-2 bg-[#15803d] hover:bg-green-800 text-white rounded-full px-6"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Maintenance Mode */}
        <div className={`rounded-2xl border-2 shadow-sm overflow-hidden ${settings.maintenance_mode ? 'border-amber-400 bg-amber-50' : 'border-green-100 bg-white'}`} data-testid="maintenance-mode-card">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldAlert className={`w-5 h-5 ${settings.maintenance_mode ? 'text-amber-600' : 'text-slate-400'}`} />
              <div>
                <h3 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Maintenance Mode</h3>
                <p className="text-slate-500 text-sm">When enabled, the public booking page shows an unavailable message. You can still create test bookings if logged in as admin. Volunteer and requester emails are suppressed.</p>
              </div>
            </div>
            <button
              data-testid="maintenance-mode-toggle"
              onClick={() => setSettings((s) => ({ ...s, maintenance_mode: !s.maintenance_mode }))}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 ${settings.maintenance_mode ? 'bg-amber-500' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${settings.maintenance_mode ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {/* External Volunteer Access */}
        <div className={`rounded-2xl border-2 shadow-sm overflow-hidden ${settings.availability_enabled ? 'border-green-200 bg-white' : 'border-slate-100 bg-white'}`} data-testid="availability-access-card">
          <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100">
            <div className="flex items-center gap-3">
              <Lock className={`w-5 h-5 ${settings.availability_enabled ? 'text-green-700' : 'text-slate-400'}`} />
              <div>
                <h3 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>External Volunteer Availability Page</h3>
                <p className="text-slate-500 text-sm">Password-gated page at <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">/availability</code> that shows free vs. booked slots for the next 1–4 weeks. Share the URL + password with volunteers who want to bring out the bike at short notice.</p>
              </div>
            </div>
            <button
              data-testid="availability-enabled-toggle"
              onClick={() => setSettings((s) => ({ ...s, availability_enabled: !s.availability_enabled }))}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 ${settings.availability_enabled ? 'bg-green-600' : 'bg-slate-300'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${settings.availability_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          {settings.availability_enabled && (
            <div className="px-6 py-5 space-y-4 bg-green-50/30">
              <div>
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Shared password</label>
                <div className="mt-1.5 flex gap-2">
                  <input
                    type="text"
                    value={availabilityPassword}
                    onChange={(e) => setAvailabilityPassword(e.target.value)}
                    placeholder={settings.availability_password_set ? "Leave blank to keep the current password" : "Set a password (e.g. dvc-volunteers-2026)"}
                    data-testid="availability-password-field"
                    className="flex-1 h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-green-600"
                  />
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs">
                  {settings.availability_password_set ? (
                    <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                      <CheckCircle className="w-3.5 h-3.5" /> A password is currently set
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                      <AlertCircle className="w-3.5 h-3.5" /> No password set — set one and Save to activate access
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-2">Type a new password above and click <strong>Save Settings</strong> to rotate. Existing volunteers on the page will be prompted to re-enter.</p>
              </div>
              <div className="pt-3 border-t border-green-100 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-slate-500 font-medium">Share this URL with volunteers:</span>
                <code className="bg-white border border-slate-200 rounded px-2 py-1 text-slate-700 select-all">{window.location.origin}/availability</code>
                <button
                  type="button"
                  data-testid="copy-availability-url-btn"
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/availability`); toast.success("URL copied"); }}
                  className="inline-flex items-center gap-1 text-green-700 hover:text-green-900 font-medium">
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
                <a
                  href="/availability"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700 font-medium">
                  <ExternalLink className="w-3.5 h-3.5" /> Open
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Weekly Schedule */}
        <SettingsCard icon={CalendarDays} title="Weekly Schedule" subtitle="Set specific time slots for each day. Days with no slots are shown as Closed.">
          <WeeklyScheduleEditor
            schedule={weeklySchedule}
            onChange={setWeeklySchedule}
          />
        </SettingsCard>

        {/* Blocked Times for Special Events */}
        <SettingsCard icon={X} title="Blocked Times" subtitle="Block specific dates or time ranges for special events. These override the weekly schedule.">
          <BlockedTimesEditor
            blockedTimes={blockedTimes}
            onUpdate={setBlockedTimes}
          />
        </SettingsCard>

        {/* Scheduling Rules */}
        <SettingsCard icon={Clock} title="Scheduling Rules">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-600 font-medium mb-2 block">Advance Notice (hours)</Label>
              <Input
                type="number"
                min="1"
                max="168"
                data-testid="advance-hours-input"
                value={settings.min_advance_hours ?? 36}
                onChange={(e) => setSettings({ ...settings, min_advance_hours: parseFloat(e.target.value) })}
                className="h-11 bg-slate-50 border-slate-200"
              />
              <p className="text-xs text-slate-400 mt-1">Earliest a slot can be booked (default 36h)</p>
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-2 block">Gap Between Bookings (min)</Label>
              <Input
                type="number"
                min="0"
                max="120"
                data-testid="gap-minutes-input"
                value={settings.gap_minutes ?? 20}
                onChange={(e) => setSettings({ ...settings, gap_minutes: parseInt(e.target.value) })}
                className="h-11 bg-slate-50 border-slate-200"
              />
              <p className="text-xs text-slate-400 mt-1">Buffer after each booking (default 20 min)</p>
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-2 block">Default Slot Duration (hours)</Label>
              <Input
                type="number"
                min="0.5"
                max="8"
                step="0.5"
                data-testid="slot-duration-input"
                value={settings.slot_duration_hours ?? 1}
                onChange={(e) => setSettings({ ...settings, slot_duration_hours: parseFloat(e.target.value) })}
                className="h-11 bg-slate-50 border-slate-200"
              />
              <p className="text-xs text-slate-400 mt-1">Duration applied to each booking (default 1h)</p>
            </div>
          </div>
        </SettingsCard>

        {/* System */}
        <SettingsCard icon={Globe} title="System">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600 font-medium mb-2 block">Timezone</Label>
              <Input
                data-testid="timezone-input"
                type="text"
                placeholder="e.g. Europe/Dublin"
                value={settings.timezone || ""}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                className="h-11 bg-slate-50 border-slate-200"
              />
            </div>
            {isSuperAdmin && (
              <div>
                <Label className="text-slate-600 font-medium mb-2 block">Frontend URL (for claim links)</Label>
                <Input
                  data-testid="frontend-url-input"
                  type="url"
                  placeholder="https://your-site.com"
                  value={settings.frontend_url || ""}
                  onChange={(e) => setSettings({ ...settings, frontend_url: e.target.value })}
                  className="h-11 bg-slate-50 border-slate-200"
                />
              </div>
            )}
          </div>
        </SettingsCard>

        {/* SMTP Email — super admin only */}
        {isSuperAdmin && (
        <SettingsCard icon={Mail} title="Email (SMTP)" subtitle="Configure your mail server to enable volunteer notifications and reminders.">
          {/* Provider Quick Setup */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-800 mb-2">Quick Setup Guides:</p>
            <div className="text-xs text-blue-700 space-y-1">
              <p><strong>Amazon SES:</strong> Host: email-smtp.{'{region}'}.amazonaws.com • Port: 587 • Use STARTTLS</p>
              <p><strong>Gmail:</strong> Host: smtp.gmail.com • Port: 587 • Use App Password</p>
              <p><strong>Infomaniak:</strong> Host: mail.infomaniak.com • Port: 465 (SSL) or 587 (STARTTLS)</p>
              <p><strong>SendGrid:</strong> Host: smtp.sendgrid.net • Port: 587 • Username: apikey</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-600 font-medium mb-2 block">SMTP Host</Label>
              <Input
                data-testid="smtp-host-input"
                type="text"
                placeholder="email-smtp.eu-west-1.amazonaws.com"
                value={settings.smtp_host || ""}
                onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                className="h-11 bg-slate-50 border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-2 block">SMTP Port</Label>
              <select
                data-testid="smtp-port-input"
                value={settings.smtp_port || 587}
                onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-md text-sm"
              >
                <option value={587}>587 (STARTTLS - Recommended for SES/SendGrid)</option>
                <option value={465}>465 (SSL/TLS - Infomaniak/Gmail)</option>
                <option value={25}>25 (Unencrypted - Not recommended)</option>
                <option value={2587}>2587 (Alternative STARTTLS)</option>
              </select>
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-2 block">SMTP Username</Label>
              <Input
                data-testid="smtp-user-input"
                type="text"
                placeholder="For SES: Your SMTP username (not AWS Access Key)"
                value={settings.smtp_user || ""}
                onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                className="h-11 bg-slate-50 border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-2 block">SMTP Password</Label>
              <div className="relative">
                <Input
                  data-testid="smtp-password-input"
                  type="password"
                  placeholder={settings.smtp_password_set ? "••••••••" : "For SES: Your SMTP password"}
                  value={smtpPassword}
                  onChange={(e) => setSmtpPassword(e.target.value)}
                  className="h-11 bg-slate-50 border-slate-200 pr-10"
                />
                {settings.smtp_password_set && !smtpPassword && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                )}
              </div>
              {settings.smtp_password_set && !smtpPassword ? (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Password is configured. Leave blank to keep existing.
                </p>
              ) : !settings.smtp_password_set && !smtpPassword ? (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> No password set. Enter your SMTP password.
                </p>
              ) : (
                <p className="text-xs text-slate-500 mt-1">
                  New password will be saved when you click Save Settings.
                </p>
              )}
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-2 block">Sender Email</Label>
              <Input
                data-testid="smtp-sender-email-input"
                type="email"
                placeholder="noreply@example.com"
                value={settings.smtp_sender_email || ""}
                onChange={(e) => setSettings({ ...settings, smtp_sender_email: e.target.value })}
                className="h-11 bg-slate-50 border-slate-200"
              />
            </div>
            <div>
              <Label className="text-slate-600 font-medium mb-2 block">Sender Name</Label>
              <Input
                data-testid="smtp-sender-name-input"
                type="text"
                placeholder="Accessible Bike Booking"
                value={settings.smtp_sender_name || ""}
                onChange={(e) => setSettings({ ...settings, smtp_sender_name: e.target.value })}
                className="h-11 bg-slate-50 border-slate-200"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <Switch
              id="smtp-tls"
              data-testid="smtp-tls-toggle"
              checked={settings.smtp_use_tls ?? true}
              onCheckedChange={(v) => setSettings({ ...settings, smtp_use_tls: v })}
            />
            <Label htmlFor="smtp-tls" className="text-slate-600 font-medium cursor-pointer">
              Use STARTTLS (recommended for port 587)
            </Label>
          </div>
          
          {/* SMTP Test Connection */}
          <div className="mt-6 pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-medium text-slate-700">Test Connection</p>
                <p className="text-xs text-slate-500">Verify your SMTP settings are correct</p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={testSmtpConnection}
                disabled={smtpTesting}
                className="gap-2"
                data-testid="test-smtp-btn"
              >
                {smtpTesting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Wifi className="w-4 h-4" />
                )}
                Test SMTP
              </Button>
            </div>
            
            {/* Test Results */}
            {smtpTestResult && (
              <div className={`rounded-lg p-4 ${smtpTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-3">
                  {smtpTestResult.success ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Connection Successful</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-5 h-5 text-red-600" />
                      <span className="font-medium text-red-800">Connection Failed</span>
                    </>
                  )}
                </div>
                
                {/* Steps */}
                <div className="space-y-1 text-sm">
                  {smtpTestResult.steps?.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className={`w-16 font-mono text-xs px-1.5 py-0.5 rounded ${
                        step.status === 'OK' ? 'bg-green-100 text-green-700' :
                        step.status === 'FAIL' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {step.status}
                      </span>
                      <span className="text-slate-600">{step.step}:</span>
                      <span className="text-slate-800">{step.message}</span>
                    </div>
                  ))}
                </div>
                
                {/* Error message */}
                {smtpTestResult.error && (
                  <div className="mt-3 p-3 bg-white rounded border border-red-200">
                    <p className="text-sm text-red-700 font-medium">Error:</p>
                    <p className="text-sm text-red-600">{smtpTestResult.error}</p>
                  </div>
                )}
                
                {/* Config info */}
                {smtpTestResult.config && (
                  <div className="mt-3 text-xs text-slate-500">
                    <span className="font-mono">
                      {smtpTestResult.config.host}:{smtpTestResult.config.port} • {smtpTestResult.config.user}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </SettingsCard>
        )} {/* end isSuperAdmin SMTP */}

        {/* Sponsor Message */}
        <SettingsCard icon={Heart} title="Sponsor Message" subtitle="Acknowledge sponsors who support this booking system. When enabled, the message appears on the booking page and in all outbound emails.">
          <div className="flex items-center gap-3 mb-4">
            <Switch
              id="sponsor-enabled"
              data-testid="sponsor-enabled-toggle"
              checked={settings.sponsor_message_enabled || false}
              onCheckedChange={(v) => setSettings({ ...settings, sponsor_message_enabled: v })}
            />
            <Label htmlFor="sponsor-enabled" className="text-slate-600 font-medium cursor-pointer">
              Show sponsor message
            </Label>
          </div>
          <div>
            <Label className="text-slate-600 font-medium mb-2 block">Sponsor Message Text</Label>
            <textarea
              data-testid="sponsor-message-input"
              rows={3}
              placeholder="e.g. This service is kindly sponsored by Acme Corp — supporting inclusive cycling in Dublin."
              value={settings.sponsor_message || ""}
              onChange={(e) => setSettings({ ...settings, sponsor_message: e.target.value })}
              disabled={!settings.sponsor_message_enabled}
              className={`w-full text-sm border rounded-xl px-3 py-2.5 focus:outline-none resize-none transition-colors ${
                settings.sponsor_message_enabled
                  ? "bg-slate-50 border-slate-200 focus:border-green-600 focus:bg-white text-slate-800"
                  : "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
              }`}
            />
            <p className="text-slate-400 text-xs mt-1.5">
              Plain text only. Appears as a banner on the booking page and at the foot of every email.
            </p>
          </div>
          <div className="mt-4">
            <Label className="text-slate-600 font-medium mb-2 block">Sponsor URL <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input
              data-testid="sponsor-url-input"
              type="url"
              placeholder="https://www.sponsor.ie"
              value={settings.sponsor_url || ""}
              onChange={(e) => setSettings({ ...settings, sponsor_url: e.target.value })}
              disabled={!settings.sponsor_message_enabled}
              className={`h-11 ${settings.sponsor_message_enabled ? "bg-slate-50 border-slate-200" : "bg-slate-100 border-slate-200 text-slate-400"}`}
            />
            <p className="text-slate-400 text-xs mt-1.5">If provided, the sponsor message becomes a clickable link on the booking page and in emails.</p>
          </div>
        </SettingsCard>

        {/* Testing Mode */}
        <SettingsCard icon={FlaskConical} title="Testing Mode" subtitle="Temporarily disable the booking rate limit (2 per hour per IP) while running tests. Re-enable when done to restore protection against spam.">
          <div className="flex items-center gap-3">
            <Switch
              id="rate-limit-toggle"
              data-testid="rate-limit-enabled-toggle"
              checked={settings.booking_rate_limit_enabled !== false}
              onCheckedChange={(v) => setSettings({ ...settings, booking_rate_limit_enabled: v })}
            />
            <Label htmlFor="rate-limit-toggle" className="text-slate-600 font-medium cursor-pointer">
              {settings.booking_rate_limit_enabled !== false
                ? "Rate limiting enabled (recommended)"
                : "Rate limiting disabled — testing mode active"}
            </Label>
          </div>
          {settings.booking_rate_limit_enabled === false && (
            <p className="mt-3 text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs">
              Rate limiting is currently OFF. Any number of bookings can be submitted per hour. Remember to re-enable this when testing is complete.
            </p>
          )}
        </SettingsCard>
      </div>
    </div>
  );
}

function SettingsCard({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl border border-green-100 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-green-700" />
          <div>
            <h2 className="font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h2>
            {subtitle && <p className="text-slate-500 text-sm mt-0.5">{subtitle}</p>}
          </div>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}
